import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="font-serif text-5xl md:text-6xl text-gray-900 mb-6">
              About Karen Vanderwerp
            </h1>
            <div className="w-24 h-1 bg-amber-600 mx-auto"></div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start mb-20">
            {/* Portrait */}
            <div className="relative flex justify-center lg:justify-start">
              {/* Thin amber vertical accent line */}
              <div className="absolute left-0 top-8 bottom-8 w-[3px] bg-amber-400 rounded-full z-20"></div>

              {/* Main photo */}
              <div className="relative w-full max-w-[460px] h-[580px] rounded-xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)] ml-6 z-10">
                <img
                  src="https://st.hzcdn.com/fimgs/64636a5307ae4486_7168-w600-h800-b1-p0--.jpg"
                  alt="Karen Vanderwerp"
                  className="w-full h-full object-cover object-top"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent"></div>

                {/* Name badge */}
                <div className="absolute bottom-0 left-0 right-0 px-7 py-6">
                  <p className="text-amber-300 text-[10px] uppercase tracking-[0.2em] mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>Principal Designer</p>
                  <p className="text-white text-xl font-semibold leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Karen Vanderwerp</p>
                </div>

                {/* Floating credential badge */}
                <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg">
                  <p className="text-amber-600 text-[10px] uppercase tracking-widest font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>Best of Houzz</p>
                  <p className="text-gray-800 text-xs font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>2019 · 2020 · 2021</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8">
              <div>
                <h2 className="font-serif text-3xl text-gray-900 mb-6">
                  Creating Timeless Spaces for Over 15 Years
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Karen Vanderwerp is the founder and principal designer of K LINN Interiors, bringing over 15 years of refined expertise to residential and commercial design. Based in Grand Rapids, Michigan, Karen has built a reputation for creating sophisticated, livable spaces that reflect her clients' unique personalities and lifestyles.
                  </p>
                  <p>
                    Her work spans from lakefront estates in Michigan to luxury residences in Saint Thomas, Virgin Islands. Each project is approached with meticulous attention to detail, a deep understanding of architectural integrity, and a commitment to timeless design that transcends trends.
                  </p>
                  <p>
                    Karen's consultative approach ensures that every design decision is intentional and aligned with her clients' vision. She believes that great design is not about imposing a signature style, but about listening, understanding, and translating her clients' aspirations into beautifully realized spaces.
                  </p>
                </div>
              </div>

              {/* Awards & Recognition */}
              <div className="bg-amber-50 p-8 rounded-lg">
                <h3 className="font-serif text-2xl text-gray-900 mb-6">
                  Awards & Recognition
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <i className="ri-award-fill text-2xl text-amber-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Best of Houzz</h4>
                      <p className="text-gray-600 text-sm">Multiple-year recipient of the prestigious Best of Houzz award for Design and Service</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <i className="ri-tv-fill text-2xl text-amber-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Media Appearances</h4>
                      <p className="text-gray-600 text-sm">Featured on television and in leading design publications for expertise in luxury residential design</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <i className="ri-star-fill text-2xl text-amber-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">33 Five-Star Reviews</h4>
                      <p className="text-gray-600 text-sm">Consistently praised by clients for professionalism, creativity, and exceptional service</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Design Philosophy */}
          <div className="bg-gray-50 p-12 md:p-16 rounded-lg mb-20">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-8">
                Design Philosophy
              </h2>
              <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
                <p>
                  "Great design is a collaboration. It begins with listening—truly understanding how my clients live, what inspires them, and what they need from their space. My role is to translate those insights into environments that are not only beautiful but deeply functional and personal."
                </p>
                <p>
                  "I believe in timeless design that honors architectural integrity while embracing modern living. Every material, every color, every piece of furniture is chosen with intention. The result is a space that feels both elevated and effortlessly livable—a place where my clients feel truly at home."
                </p>
              </div>
            </div>
          </div>

          {/* Approach */}
          <div className="mb-20">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 text-center mb-12">
              A Personalized Approach
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-user-heart-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="font-serif text-xl text-gray-900 mb-4">Client-Centered</h3>
                <p className="text-gray-600 leading-relaxed">
                  Every project begins with understanding your lifestyle, preferences, and vision. Your home should reflect who you are.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-pencil-ruler-2-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="font-serif text-xl text-gray-900 mb-4">Detail-Oriented</h3>
                <p className="text-gray-600 leading-relaxed">
                  From architectural elements to the smallest accessory, every detail is carefully considered and intentionally chosen.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-time-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="font-serif text-xl text-gray-900 mb-4">Timeless Quality</h3>
                <p className="text-gray-600 leading-relaxed">
                  Designs that transcend trends, combining classic elegance with modern functionality for lasting beauty.
                </p>
              </div>
            </div>
          </div>

          {/* Service Areas */}
          <div className="bg-amber-50 p-12 rounded-lg mb-20">
            <h2 className="font-serif text-3xl text-gray-900 text-center mb-8">
              Service Areas
            </h2>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <i className="ri-map-pin-line text-3xl text-amber-600 mb-4"></i>
                <h3 className="font-semibold text-gray-900 mb-2">Grand Rapids, Michigan</h3>
                <p className="text-gray-600 text-sm">Serving the greater Grand Rapids area and surrounding communities</p>
              </div>
              <div>
                <i className="ri-ship-line text-3xl text-amber-600 mb-4"></i>
                <h3 className="font-semibold text-gray-900 mb-2">Lakefront Homes</h3>
                <p className="text-gray-600 text-sm">Specializing in luxury lakefront properties throughout Michigan</p>
              </div>
              <div>
                <i className="ri-sun-line text-3xl text-amber-600 mb-4"></i>
                <h3 className="font-semibold text-gray-900 mb-2">Saint Thomas</h3>
                <p className="text-gray-600 text-sm">Designing exceptional island residences in the Virgin Islands</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6">
              Let's Create Something Beautiful Together
            </h2>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              Whether you're building a new home, renovating an existing space, or simply seeking design guidance, I'd love to hear about your project.
            </p>
            <a
              href="/contact"
              className="inline-block bg-amber-600 text-white px-10 py-4 rounded-lg hover:bg-amber-700 transition-colors duration-300 font-medium whitespace-nowrap"
            >
              Start Your Project
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
